<template>
  <div class="game-assets" v-if="hasAny">
    <!-- 数据概览（基础属性/分路段位/印记/职业分布）—— 对齐盼之顶部 stat block -->
    <section v-if="hasOverview" class="ga-section ga-overview">
      <div class="ga-head"><span class="ga-bar"></span><h4>数据概览</h4></div>

      <div v-if="stats.basic && stats.basic.length" class="ga-basic">
        <div v-for="(b, i) in stats.basic" :key="'b' + i" class="ga-basic-item">
          <span class="v">{{ b.value }}</span>
          <span class="k">{{ b.label }}</span>
        </div>
      </div>

      <div v-if="stats.lane_rank && stats.lane_rank.length" class="ga-statline">
        <span class="ga-statline-label">分路段位</span>
        <span v-for="(l, i) in stats.lane_rank" :key="'l' + i" class="ga-pill">{{ l.name }}<em>{{ l.count }}段</em></span>
      </div>

      <div v-if="stats.marks && stats.marks.length" class="ga-statline">
        <span class="ga-statline-label">印记</span>
        <span v-for="(m, i) in stats.marks" :key="'m' + i" class="ga-pill">{{ m.name }}<em>{{ m.count }}</em></span>
      </div>

      <div v-if="stats.profession && stats.profession.length" class="ga-statline">
        <span class="ga-statline-label">职业分布</span>
        <span v-for="(p, i) in stats.profession" :key="'p' + i" class="ga-pill">{{ p.name }}<em>{{ p.count }}</em></span>
      </div>
    </section>

    <!-- 资产分组（皮肤/英雄/星元/战力装扮） -->
    <section
      v-for="group in gridGroups"
      :key="group.asset_type"
      class="ga-section">
      <div class="ga-head">
        <span class="ga-bar"></span>
        <h4>{{ group.asset_type }}</h4>
        <span class="ga-count"><em>{{ group.owned || 0 }}</em>/{{ group.total || 0 }}</span>
        <label class="ga-only" v-if="(group.owned || 0) < (group.total || 0)">
          <input type="checkbox" v-model="state[group.asset_type].ownedOnly"> 只看已拥有
        </label>
      </div>

      <!-- 稀有度筛选（仅皮肤有 quality 分桶） -->
      <div class="ga-filter" v-if="qualityChips(group).length > 1">
        <span
          v-for="chip in qualityChips(group)"
          :key="chip.key"
          class="ga-chip"
          :class="{ on: state[group.asset_type].quality === chip.key }"
          @click="state[group.asset_type].quality = chip.key">
          <span>{{ chip.label }} <i>{{ chip.owned }}/{{ chip.total }}</i></span>
        </span>
      </div>

      <!-- 资产网格 -->
      <div class="ga-grid">
        <div
          v-for="(item, i) in visibleItems(group)"
          :key="group.asset_type + '_' + i"
          class="ga-cell"
          :class="{ off: !item.owned }">
          <div class="ga-thumb">
            <img
              v-if="item.image && !item._err"
              :src="item.image"
              loading="lazy"
              :alt="item.name"
              @error="onImgErr(item)">
            <span v-else class="ga-ph">{{ shortName(item.name) }}</span>
            <span v-if="item.quality && group.asset_type === '皮肤'" class="ga-q">{{ item.quality }}</span>
            <span v-if="!item.owned" class="ga-off-tag">未拥有</span>
          </div>
          <div class="ga-name" :title="item.name">{{ item.name }}</div>
        </div>
      </div>

      <div
        class="ga-more"
        v-if="filteredItems(group).length > state[group.asset_type].cap"
        @click="state[group.asset_type].cap += CAP_STEP">
        展开更多（剩余 {{ filteredItems(group).length - state[group.asset_type].cap }}）
        <i class="el-icon-arrow-down"></i>
      </div>
    </section>

    <!-- 卖点皮肤：纯文本高亮条（无图无 owned） -->
    <section v-if="salePoints.length" class="ga-section">
      <div class="ga-head">
        <span class="ga-bar"></span><h4>卖点皮肤</h4>
        <span class="ga-count">{{ salePoints.length }}</span>
      </div>
      <div class="ga-sale">
        <span v-for="(s, i) in salePoints" :key="i" class="ga-sale-tag">{{ s.name }}</span>
      </div>
    </section>

    <!-- 实验室截图（英雄战力/信誉分/万象图鉴…） -->
    <section v-if="lab && lab.length" class="ga-section">
      <div class="ga-head"><span class="ga-bar"></span><h4>账号实拍</h4></div>
      <div v-for="(g, gi) in lab" :key="'lab' + gi" class="ga-lab">
        <div class="ga-lab-name">{{ g.group_name }}</div>
        <div class="ga-lab-imgs">
          <el-image
            v-for="(url, ui) in g.images"
            :key="ui"
            :src="url"
            :preview-src-list="g.images"
            fit="cover"
            lazy
            class="ga-lab-img"/>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
const ORDER = ['皮肤', '英雄', '星元', '战力装扮']
const CAP_INIT = 18
const CAP_STEP = 48

export default {
  name: 'GameAssets',
  props: {
    // DetailDTO.assets.groups —— [{asset_type,total,owned,items:[{name,image,quality,owned}]}]
    groups: { type: Array, default: () => [] },
    // DetailDTO.gallery.lab —— [{group_name,images:[url]}]
    lab: { type: Array, default: () => [] },
    // DetailDTO.summary —— { stats:{basic,lane_rank,marks,profession}, ... }
    summary: { type: Object, default: () => ({}) }
  },
  data() {
    return {
      CAP_STEP,
      state: {} // 每个 asset_type 的本地交互态 {quality, ownedOnly, cap}
    }
  },
  computed: {
    stats() {
      return (this.summary && this.summary.stats) || {}
    },
    hasOverview() {
      const s = this.stats
      return !!((s.basic && s.basic.length) || (s.lane_rank && s.lane_rank.length) ||
        (s.marks && s.marks.length) || (s.profession && s.profession.length))
    },
    hasAny() {
      return (this.groups && this.groups.length) || (this.lab && this.lab.length) || this.hasOverview
    },
    // 进网格的分组（按固定顺序，排除卖点皮肤）
    gridGroups() {
      return (this.groups || [])
        .filter(g => g.asset_type !== '卖点皮肤' && g.items && g.items.length)
        .sort((a, b) => this.orderIndex(a.asset_type) - this.orderIndex(b.asset_type))
    },
    salePoints() {
      const g = (this.groups || []).find(x => x.asset_type === '卖点皮肤')
      return g && g.items ? g.items : []
    }
  },
  watch: {
    groups: { immediate: true, handler: 'initState' }
  },
  methods: {
    initState() {
      const next = {}
      ;(this.groups || []).forEach(g => {
        next[g.asset_type] = (this.state[g.asset_type]) || { quality: '全部', ownedOnly: false, cap: CAP_INIT }
      })
      this.state = next
    },
    orderIndex(type) {
      const i = ORDER.indexOf(type)
      return i === -1 ? ORDER.length : i
    },
    shortName(name) {
      return (name || '').replace(/^.*?[_·]/, '').slice(0, 6) || (name || '').slice(0, 6)
    },
    onImgErr(item) {
      this.$set(item, '_err', true)
    },
    // 稀有度筛选项（含 owned/total），仅当存在 >1 个 quality 时展示
    qualityChips(group) {
      if (group.asset_type !== '皮肤') return []
      const map = new Map()
      group.items.forEach(it => {
        const q = it.quality || '其他'
        if (!map.has(q)) map.set(q, { total: 0, owned: 0 })
        const c = map.get(q)
        c.total++
        if (it.owned) c.owned++
      })
      if (map.size <= 1) return []
      const chips = [{ key: '全部', label: '全部', total: group.total || group.items.length, owned: group.owned || 0 }]
      map.forEach((c, key) => chips.push({ key, label: key, total: c.total, owned: c.owned }))
      return chips
    },
    filteredItems(group) {
      const st = this.state[group.asset_type] || {}
      let items = group.items || []
      if (group.asset_type === '皮肤' && st.quality && st.quality !== '全部') {
        items = items.filter(it => (it.quality || '其他') === st.quality)
      }
      if (st.ownedOnly) items = items.filter(it => it.owned)
      // 已拥有优先展示
      return items.slice().sort((a, b) => (b.owned ? 1 : 0) - (a.owned ? 1 : 0))
    },
    visibleItems(group) {
      const st = this.state[group.asset_type] || {}
      return this.filteredItems(group).slice(0, st.cap || CAP_INIT)
    }
  }
}
</script>

<style lang="less" scoped>
.game-assets {
  display: flex;
  flex-direction: column;
  gap: 26px;
}

/* 数据概览面板（站点暖橙白风格） */
.ga-overview {
  background: var(--bg-card, #FFFFFF);
  border: 1px solid var(--border-soft, #E8E4DE);
  border-radius: 4px;
  padding: 16px 18px;

  .ga-basic {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(118px, 1fr));
    gap: 10px;
    margin-bottom: 16px;

    .ga-basic-item {
      display: flex;
      flex-direction: column;
      gap: 3px;
      background: var(--bg-tag, #F0EDE8);
      border-radius: 4px;
      padding: 10px 12px;
      min-width: 0;
      .v {
        font-size: 21px;
        font-weight: 900;
        letter-spacing: -0.5px;
        line-height: 1.05;
        color: var(--text-main, #1A1D24);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .k { font-size: 12px; color: var(--text-muted, #9AA0AC); }
    }
  }

  .ga-statline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    padding: 10px 0;
    border-top: 1px dashed var(--border-soft, #E8E4DE);

    .ga-statline-label {
      flex-shrink: 0;
      font-size: 13px;
      font-weight: 700;
      color: var(--text-secondary, #5A5F6B);
      margin-right: 4px;
    }
    .ga-pill {
      font-size: 12px;
      color: var(--text-secondary, #5A5F6B);
      background: var(--bg-tag, #F0EDE8);
      border-radius: 3px;
      padding: 4px 10px;
      em {
        font-style: normal;
        font-weight: 700;
        color: var(--primary, #FF5A1F);
        margin-left: 4px;
      }
    }
  }
}

.ga-section {
  .ga-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;

    .ga-bar {
      width: 5px;
      height: 16px;
      background: var(--primary, #FF5A1F);
      transform: skewX(-12deg);
    }
    h4 {
      font-size: 16px;
      font-weight: 900;
      color: var(--text-main, #1A1D24);
      margin: 0;
    }
    .ga-count {
      font-size: 13px;
      color: var(--text-muted, #9AA0AC);
      font-weight: 700;
      em { font-style: normal; color: var(--primary, #FF5A1F); font-size: 16px; }
    }
    .ga-only {
      margin-left: auto;
      font-size: 12px;
      color: var(--text-secondary, #5A5F6B);
      cursor: pointer;
      user-select: none;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      input { cursor: pointer; }
    }
  }

  .ga-filter {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
    margin-bottom: 16px;

    .ga-chip {
      font-size: 12px;
      color: var(--text-secondary, #5A5F6B);
      background: var(--bg-tag, #F0EDE8);
      padding: 5px 12px;
      border-radius: 2px;
      cursor: pointer;
      transform: skewX(-12deg);
      transition: all .18s var(--ease, ease);
      > span { display: block; transform: skewX(12deg); }
      i { font-style: normal; color: var(--text-muted, #9AA0AC); margin-left: 2px; }

      &:hover { background: var(--border-soft, #E8E4DE); }
      &.on {
        background: var(--primary, #FF5A1F);
        color: #fff;
        i { color: rgba(255, 255, 255, .85); }
      }
    }
  }

  .ga-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 12px;

    .ga-cell {
      min-width: 0;

      .ga-thumb {
        position: relative;
        aspect-ratio: 3 / 4;
        border-radius: 3px;
        overflow: hidden;
        background: var(--bg-section, #F3F0EB);
        display: flex;
        align-items: center;
        justify-content: center;

        img { width: 100%; height: 100%; object-fit: cover; display: block; }

        .ga-ph {
          color: var(--text-muted, #9AA0AC);
          font-weight: 900;
          font-style: italic;
          font-size: 13px;
          opacity: .9;
          padding: 0 4px;
          text-align: center;
        }
        .ga-q {
          position: absolute;
          left: 0; top: 0;
          font-size: 10px;
          color: #fff;
          background: rgba(14, 17, 22, .7);
          padding: 2px 6px;
          border-bottom-right-radius: 3px;
          max-width: 90%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .ga-off-tag {
          position: absolute;
          right: 0; bottom: 0;
          font-size: 10px;
          color: #fff;
          background: rgba(26, 29, 36, .78);
          padding: 2px 6px;
          border-top-left-radius: 3px;
        }
      }

      .ga-name {
        margin-top: 6px;
        font-size: 12px;
        color: var(--text-secondary, #5A5F6B);
        line-height: 1.35;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-align: center;
      }

      &.off {
        .ga-thumb img, .ga-ph { filter: grayscale(1); opacity: .42; }
        .ga-name { color: var(--text-muted, #9AA0AC); }
      }
    }
  }

  .ga-more {
    margin: 16px auto 0;
    width: fit-content;
    font-size: 13px;
    color: var(--text-secondary, #5A5F6B);
    background: var(--bg-tag, #F0EDE8);
    padding: 8px 22px;
    border-radius: 3px;
    cursor: pointer;
    transition: all .18s var(--ease, ease);
    &:hover { background: var(--border-soft, #E8E4DE); color: var(--text-main, #1A1D24); }
  }

  .ga-sale {
    display: flex;
    flex-wrap: wrap;
    gap: 9px;
    .ga-sale-tag {
      font-size: 13px;
      color: var(--primary, #FF5A1F);
      background: var(--primary-soft, #FCEDE5);
      border: 1px solid rgba(255, 90, 31, .22);
      padding: 5px 12px;
      border-radius: 3px;
    }
  }

  .ga-lab {
    margin-bottom: 16px;
    .ga-lab-name {
      font-size: 13px;
      font-weight: 700;
      color: var(--text-main, #1A1D24);
      margin-bottom: 8px;
    }
    .ga-lab-imgs {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 10px;
      .ga-lab-img {
        width: 100%;
        height: 110px;
        border-radius: 3px;
        border: 1px solid var(--border-soft, #E8E4DE);
        cursor: pointer;
      }
    }
  }
}
</style>
